import { useState, useMemo, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { ChevronDown, Calculator, Target } from "lucide-react";

const ACTIVITY_LEVELS = [
  { range: "1,4 – 1,5", desc: "Nur sitzende Tätigkeit mit wenig/keinen anstrengenden Freizeitaktivität", examples: "Büroangestellte, Feinmechaniker" },
  { range: "1,6 – 1,7", desc: "Sitzende Tätigkeit, zeitweilig auch zusätzlicher Energieaufwand für gehende u. stehende Tätigkeiten (*)", examples: "Laboranten, Kraftfahrer, Studierende, Fließbandarbeiter" },
  { range: "1,8 – 1,9", desc: "Überwiegend gehende & stehende Arbeit (*)", examples: "Hausfrauen, Verkäufer, Kellner, Mechaniker, Handwerker" },
  { range: "2,0 – 2,4", desc: "Körperliche anstrengende berufliche Arbeit (*)", examples: "Bauarbeiter, Landwirte, Waldarbeiter, Bergarbeiter, Leistungssportler" },
];
const PAL_FOOTNOTE = "(*) = für sportliche Betätigungen oder anstrengende Freizeitaktivitäten (30–60 min., 4–5 mal je Woche) können zusätzlich pro Tag 0,3 PAL-Einheiten zugelegt werden.";

const GOALS = [
  { id: "halten", label: "Gewicht halten", color: "bg-blue-600", desc: "Erhaltungsbedarf" },
  { id: "zunehmen", label: "Zunehmen", color: "bg-emerald-600", desc: "Kalorienüberschuss" },
  { id: "abnehmen", label: "Abnehmen", color: "bg-rose-600", desc: "Kaloriendefizit" },
];

export default function EnergiebilanzPanel({ data, onChange, onCalcUpdate }) {
  const [expanded, setExpanded] = useState(true);

  const d = data || {};
  const gewicht = parseFloat(d.gewicht) || 0;
  const pal = parseFloat(d.pal) || 1.5;
  const adjustPct = parseFloat(d.adjust_pct) || 15;
  const arbeitszeit = parseFloat(d.arbeitszeit) || 8;
  const sportMann = parseFloat(d.sport_mann) || 0.3;
  const sportFrau = parseFloat(d.sport_frau) || 0.3;
  const frauFactor = parseFloat(d.frau_factor) || 0.9;
  const geschlecht = d.geschlecht || "mann";
  const proteinPct = parseFloat(d.protein_pct) || 20;
  const fettPct = parseFloat(d.fett_pct) || 19;
  const carbsPct = parseFloat(d.carbs_pct) || 61;
  const goal = d.goal || "halten";

  const ruhe = 24 - arbeitszeit;

  const calc = useMemo(() => {
    // Ohne Sport: Mann vs Frau (Excel G12 vs G18)
    const tdeeNoSportMann = gewicht * ruhe + gewicht * arbeitszeit * pal;
    const tdeeNoSportFrau = gewicht * frauFactor * ruhe + gewicht * frauFactor * arbeitszeit * pal;
    const tdeeNoSport = geschlecht === "frau" ? tdeeNoSportFrau : tdeeNoSportMann;

    // Mit Sport: Mann (Excel G14) vs Frau (Excel G19)
    const tdeeSportMann = gewicht * ruhe + gewicht * arbeitszeit * pal * pal + sportMann;
    const tdeeSportFrau = gewicht * frauFactor * ruhe + gewicht * frauFactor * arbeitszeit * (pal + sportFrau);
    const tdeeWithSport = geschlecht === "frau" ? tdeeSportFrau : tdeeSportMann;

    const gewichtHalten = tdeeWithSport;
    const zunehmen = tdeeWithSport * (1 + adjustPct / 100);
    const abnehmen = tdeeWithSport * (1 - adjustPct / 100);

    // Target based on selected goal
    let targetKcal = gewichtHalten;
    if (goal === "zunehmen") targetKcal = zunehmen;
    if (goal === "abnehmen") targetKcal = abnehmen;

    const targetProteinKcal = targetKcal * (proteinPct / 100);
    const targetFettKcal = targetKcal * (fettPct / 100);
    const targetCarbsKcal = targetKcal * (carbsPct / 100);

    return {
      ruhe, tdeeNoSport, tdeeSportMann, tdeeSportFrau, tdeeWithSport,
      gewichtHalten, zunehmen, abnehmen,
      targetKcal,
      targetProteinG: targetProteinKcal / 4.1,
      targetFettG: targetFettKcal / 9.3,
      targetCarbsG: targetCarbsKcal / 4.1,
      // For the tables
      zunProteinKcal: zunehmen * (proteinPct / 100),
      zunFettKcal: zunehmen * (fettPct / 100),
      zunCarbsKcal: zunehmen * (carbsPct / 100),
      zunProteinG: (zunehmen * (proteinPct / 100)) / 4.1,
      zunFettG: (zunehmen * (fettPct / 100)) / 9.3,
      zunCarbsG: (zunehmen * (carbsPct / 100)) / 4.1,
      abnProteinKcal: abnehmen * (proteinPct / 100),
      abnFettKcal: abnehmen * (fettPct / 100),
      abnCarbsKcal: abnehmen * (carbsPct / 100),
      abnProteinG: (abnehmen * (proteinPct / 100)) / 4.1,
      abnFettG: (abnehmen * (fettPct / 100)) / 9.3,
      abnCarbsG: (abnehmen * (carbsPct / 100)) / 4.1,
    };
  }, [gewicht, pal, arbeitszeit, sportMann, sportFrau, frauFactor, geschlecht, adjustPct, proteinPct, fettPct, carbsPct, ruhe, goal]);

  // Notify parent of calculated targets
  useEffect(() => {
    if (onCalcUpdate) {
      if (gewicht > 0) {
        onCalcUpdate({
          targetKcal: calc.targetKcal,
          targetProteinG: calc.targetProteinG,
          targetCarbsG: calc.targetCarbsG,
          targetFettG: calc.targetFettG,
          goal,
        });
      } else {
        onCalcUpdate(null);
      }
    }
  }, [calc.targetKcal, calc.targetProteinG, calc.targetCarbsG, calc.targetFettG, goal, gewicht]);

  const set = (key, val) => onChange({ ...d, [key]: val });
  const n = (v) => v.toFixed(1);
  const ni = (v) => Math.round(v);

  return (
    <div className="bg-white rounded-xl border border-slate-200 mb-6 overflow-hidden" data-testid="energiebilanz-panel">
      <div onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-slate-900 to-slate-800 text-white cursor-pointer">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-bold">Energiebilanz</span>
          {gewicht > 0 && <span className="text-xs text-slate-300 ml-2">TDEE: {ni(calc.tdeeWithSport)} kcal</span>}
        </div>
        <div className="flex items-center gap-3">
          {gewicht > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-semibold">
              Ziel: {ni(calc.targetKcal)} kcal
            </span>
          )}
          <div className={`transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'}`}>
            <ChevronDown className="w-4 h-4" />
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-5 space-y-5">
          {/* Row 1: Client data (GREEN inputs) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-[10px] font-bold text-emerald-600 uppercase mb-1 block">Gewicht (kg) *</label>
              <Input type="number" value={d.gewicht || ""} onChange={e => set("gewicht", e.target.value)}
                className="border-emerald-300 bg-emerald-50/50 focus:border-emerald-500" data-testid="eb-gewicht" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-emerald-600 uppercase mb-1 block">Aktivitäts-Multiplikator (PAL)</label>
              <Input type="number" step="0.1" value={d.pal || "1.5"} onChange={e => set("pal", e.target.value)}
                className="border-emerald-300 bg-emerald-50/50 focus:border-emerald-500" data-testid="eb-pal" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-emerald-600 uppercase mb-1 block">Zu/Abnehmen %</label>
              <Input type="number" value={d.adjust_pct || "15"} onChange={e => set("adjust_pct", e.target.value)}
                className="border-emerald-300 bg-emerald-50/50 focus:border-emerald-500" data-testid="eb-adjust" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-emerald-600 uppercase mb-1 block">Geschlecht</label>
              <div className="flex gap-1">
                <button onClick={() => set("geschlecht", "mann")}
                  className={`flex-1 py-1.5 rounded text-xs font-bold ${geschlecht === "mann" ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}
                  data-testid="eb-geschlecht-mann">Mann</button>
                <button onClick={() => set("geschlecht", "frau")}
                  className={`flex-1 py-1.5 rounded text-xs font-bold ${geschlecht === "frau" ? 'bg-pink-500 text-white' : 'bg-slate-100 text-slate-500'}`}
                  data-testid="eb-geschlecht-frau">Frau</button>
              </div>
            </div>
          </div>

          {/* Row 2: Work/Rest/Sport */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-[10px] font-bold text-emerald-600 uppercase mb-1 block">Arbeitszeit (h)</label>
              <Input type="number" value={d.arbeitszeit || "8"} onChange={e => set("arbeitszeit", e.target.value)}
                className="border-emerald-300 bg-emerald-50/50" data-testid="eb-arbeitszeit" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Ruhe (h)</label>
              <div className="h-9 flex items-center px-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-600">{calc.ruhe}</div>
            </div>
            <div>
              <label className="text-[10px] font-bold text-emerald-600 uppercase mb-1 block">Sport (PAL+)</label>
              <Input type="number" step="0.1" value={d.sport_mann || "0.3"} onChange={e => set("sport_mann", e.target.value)}
                className="border-emerald-300 bg-emerald-50/50" data-testid="eb-sport" />
            </div>
            {geschlecht === "frau" && (
              <div>
                <label className="text-[10px] font-bold text-emerald-600 uppercase mb-1 block">Frau-Faktor</label>
                <Input type="number" step="0.1" value={d.frau_factor || "0.9"} onChange={e => set("frau_factor", e.target.value)}
                  className="border-emerald-300 bg-emerald-50/50" />
              </div>
            )}
          </div>

          {/* Goal selector */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-3.5 h-3.5 text-slate-500" />
              <p className="text-[10px] font-bold text-slate-500 uppercase">Ziel auswählen</p>
            </div>
            <div className="flex gap-2" data-testid="eb-goal-selector">
              {GOALS.map(g => (
                <button key={g.id} onClick={() => set("goal", g.id)}
                  className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all ${
                    goal === g.id ? `${g.color} text-white shadow-md scale-[1.02]` : 'bg-slate-50 text-slate-500 border border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`eb-goal-${g.id}`}>
                  {g.label}
                  {goal === g.id && gewicht > 0 && (
                    <span className="block text-[10px] font-normal mt-0.5 opacity-80">
                      {g.id === "halten" && `${ni(calc.gewichtHalten)} kcal`}
                      {g.id === "zunehmen" && `${ni(calc.zunehmen)} kcal (+${ni(adjustPct)}%)`}
                      {g.id === "abnehmen" && `${ni(calc.abnehmen)} kcal (-${ni(adjustPct)}%)`}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* TDEE Results */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-[10px] text-slate-500 font-semibold uppercase">Ohne Sport</p>
              <p className="text-lg font-black text-slate-700">{ni(calc.tdeeNoSport)} <span className="text-xs font-normal">kcal</span></p>
            </div>
            <div className={`rounded-lg p-3 border ${goal === "halten" ? 'bg-blue-100 border-blue-300 ring-2 ring-blue-200' : 'bg-blue-50 border-blue-200'}`}>
              <p className="text-[10px] text-blue-600 font-semibold uppercase">Gewicht halten</p>
              <p className="text-lg font-black text-blue-700">{ni(calc.gewichtHalten)} <span className="text-xs font-normal">kcal</span></p>
            </div>
            <div className={`rounded-lg p-3 border ${goal === "zunehmen" ? 'bg-emerald-100 border-emerald-300 ring-2 ring-emerald-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <p className="text-[10px] text-emerald-600 font-semibold uppercase">Zunehmen (+{ni(adjustPct)}%)</p>
              <p className="text-lg font-black text-emerald-700">{ni(calc.zunehmen)} <span className="text-xs font-normal">kcal</span></p>
            </div>
            <div className={`rounded-lg p-3 border ${goal === "abnehmen" ? 'bg-rose-100 border-rose-300 ring-2 ring-rose-200' : 'bg-rose-50 border-rose-200'}`}>
              <p className="text-[10px] text-rose-600 font-semibold uppercase">Abnehmen (-{ni(adjustPct)}%)</p>
              <p className="text-lg font-black text-rose-700">{ni(calc.abnehmen)} <span className="text-xs font-normal">kcal</span></p>
            </div>
          </div>

          {/* Macro percentages (GREEN) */}
          <div>
            <p className="text-[10px] font-bold text-slate-500 uppercase mb-2">Makroverteilung (%)</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-bold text-blue-600 mb-1 block">Protein %</label>
                <Input type="number" value={d.protein_pct || "20"} onChange={e => set("protein_pct", e.target.value)}
                  className="border-emerald-300 bg-emerald-50/50" data-testid="eb-protein-pct" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-amber-600 mb-1 block">Kohlenhydrate %</label>
                <Input type="number" value={d.carbs_pct || "61"} onChange={e => set("carbs_pct", e.target.value)}
                  className="border-emerald-300 bg-emerald-50/50" data-testid="eb-carbs-pct" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-rose-600 mb-1 block">Fett %</label>
                <Input type="number" value={d.fett_pct || "19"} onChange={e => set("fett_pct", e.target.value)}
                  className="border-emerald-300 bg-emerald-50/50" data-testid="eb-fett-pct" />
              </div>
            </div>
          </div>

          {/* Macro tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Zunehmen macros */}
            <div className="border border-emerald-200 rounded-lg overflow-hidden">
              <div className="bg-emerald-600 text-white px-3 py-2 text-xs font-bold">Empfehlungsbedarf Zunehmen — {ni(calc.zunehmen)} kcal</div>
              <table className="w-full text-xs">
                <thead><tr className="bg-emerald-50"><th className="px-3 py-1.5 text-left text-emerald-700">Makro</th><th className="px-2 py-1.5 text-center text-emerald-700">kcal</th><th className="px-2 py-1.5 text-center text-emerald-700">Gramm</th></tr></thead>
                <tbody>
                  <tr className="border-t border-emerald-100"><td className="px-3 py-1.5 font-semibold text-blue-700">Protein</td><td className="text-center">{ni(calc.zunProteinKcal)}</td><td className="text-center font-bold">{n(calc.zunProteinG)}g</td></tr>
                  <tr className="border-t border-emerald-100"><td className="px-3 py-1.5 font-semibold text-rose-700">Fett</td><td className="text-center">{ni(calc.zunFettKcal)}</td><td className="text-center font-bold">{n(calc.zunFettG)}g</td></tr>
                  <tr className="border-t border-emerald-100"><td className="px-3 py-1.5 font-semibold text-amber-700">Kohlenhydrate</td><td className="text-center">{ni(calc.zunCarbsKcal)}</td><td className="text-center font-bold">{n(calc.zunCarbsG)}g</td></tr>
                </tbody>
              </table>
            </div>

            {/* Abnehmen macros */}
            <div className="border border-rose-200 rounded-lg overflow-hidden">
              <div className="bg-rose-600 text-white px-3 py-2 text-xs font-bold">Empfehlungsbedarf Abnehmen — {ni(calc.abnehmen)} kcal</div>
              <table className="w-full text-xs">
                <thead><tr className="bg-rose-50"><th className="px-3 py-1.5 text-left text-rose-700">Makro</th><th className="px-2 py-1.5 text-center text-rose-700">kcal</th><th className="px-2 py-1.5 text-center text-rose-700">Gramm</th></tr></thead>
                <tbody>
                  <tr className="border-t border-rose-100"><td className="px-3 py-1.5 font-semibold text-blue-700">Protein</td><td className="text-center">{ni(calc.abnProteinKcal)}</td><td className="text-center font-bold">{n(calc.abnProteinG)}g</td></tr>
                  <tr className="border-t border-rose-100"><td className="px-3 py-1.5 font-semibold text-rose-700">Fett</td><td className="text-center">{ni(calc.abnFettKcal)}</td><td className="text-center font-bold">{n(calc.abnFettG)}g</td></tr>
                  <tr className="border-t border-rose-100"><td className="px-3 py-1.5 font-semibold text-amber-700">Kohlenhydrate</td><td className="text-center">{ni(calc.abnCarbsKcal)}</td><td className="text-center font-bold">{n(calc.abnCarbsG)}g</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* PAL Reference Table */}
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-400 hover:text-slate-600 font-semibold">PAL-Referenztabelle (Orientierung für Personal Trainer)</summary>
            <table className="w-full mt-2 border border-slate-200 rounded">
              <thead><tr className="bg-slate-50"><th className="px-3 py-1.5 text-left">PAL</th><th className="px-3 py-1.5 text-left">Beschreibung</th><th className="px-3 py-1.5 text-left">Beispiele</th></tr></thead>
              <tbody>
                {ACTIVITY_LEVELS.map((a, i) => (
                  <tr key={i} className="border-t border-slate-100 bg-emerald-50/30"><td className="px-3 py-1.5 font-bold">{a.range}</td><td className="px-3 py-1.5">{a.desc}</td><td className="px-3 py-1.5 text-slate-500">{a.examples}</td></tr>
                ))}
              </tbody>
            </table>
            <p className="mt-1.5 text-[10px] text-slate-400 italic leading-relaxed">{PAL_FOOTNOTE}</p>
          </details>
        </div>
      )}
    </div>
  );
}
